import {memo} from 'react'

type ManifestStatus = 'paid' | 'pending' | 'expired' | 'fraud' | 'invalid'

interface FieldStatus {
  fromAddress: boolean // true = mismatch
  toAddress: boolean // true = mismatch
  tracking: boolean // true = mismatch
}

interface RegionCoordinates {
  x: number
  y: number
  width: number
  height: number
}

interface LabelPreviewProps {
  image: string
  alt: string
  showAddresses: boolean
  status: ManifestStatus
  addresses: {fromZip?: string; from: string; to: string; tracking: string}
  fieldStatus?: FieldStatus
  regions?: {
    to?: RegionCoordinates
    tracking?: RegionCoordinates
  }
}

export const LabelPreview = memo<LabelPreviewProps>(
  ({image, alt, showAddresses, status, addresses, fieldStatus, regions}): JSX.Element => {
    // Determine individual field statuses
    const trackingStatus =
      status === 'fraud' && fieldStatus?.tracking ? 'fraud' : status === 'fraud' ? 'paid' : status
    const toAddressStatus =
      status === 'fraud' && fieldStatus?.toAddress ? 'fraud' : status === 'fraud' ? 'paid' : status

    // Convert region coordinates to CSS percentages
    // Coordinates are already relative to label bounds (0-1 range)
    const trackingStyle = regions?.tracking
      ? {
          left: `${regions.tracking.x * 100}%`,
          top: `${regions.tracking.y * 100}%`,
          width: `${regions.tracking.width * 100}%`,
          height: `${regions.tracking.height * 100}%`,
          transform: 'none', // Override default transform when using dynamic positioning
        }
      : undefined

    const toAddressStyle = regions?.to
      ? {
          left: `${regions.to.x * 100}%`,
          top: `${regions.to.y * 100}%`,
          width: `${regions.to.width * 100}%`,
          height: `${regions.to.height * 100}%`,
        }
      : undefined

    return (
      <div className='gori-signature-label-preview text-center'>
        <div className='gori-signature-label-wrapper'>
          <img src={image} alt={alt} className='gori-signature-label-image' />
          <div className='gori-signature-address-overlay'>
            <div className='gori-signature-address-from'>{showAddresses ? addresses.from : ''}</div>
            {status === 'invalid' || status === 'expired' ? null : (
              <div
                className={`gori-signature-indicator-wrapper tracking status-${trackingStatus}`}
                style={trackingStyle}
              >
                <div className='gori-signature-address-tracking'>
                  {showAddresses ? addresses.tracking : ''}
                </div>
              </div>
            )}
            {status === 'expired' ? (
              <div className='gori-signature-address-expired'></div>
            ) : status === 'invalid' ? null : (
              <div
                className={`gori-signature-indicator-wrapper address-to status-${toAddressStatus}`}
                style={toAddressStyle}
              >
                <div className='gori-signature-address-to'>{showAddresses ? addresses.to : ''}</div>
              </div>
            )}
          </div>
        </div>
        <div className='gori-signature-label-text'>{alt}</div>
      </div>
    )
  }
)
LabelPreview.displayName = 'LabelPreview'
